import { ref } from 'vue';
import { API_ENDPOINTS } from '@/apiConfig';
import axiosClient from '@/axiosClient';
import { useEmailSender } from '@/composables/emails/useEmailSender';

export function useSingleTicket() {
    const ticket = ref(null);
    const loading = ref(false);
    const error = ref(null);
    const publishingStates = ref({});

    // Clear the current ticket data and set loading state
    const clearTicket = () => {
        ticket.value = null;
        loading.value = true;
        error.value = null;
    };

    // Fetch a single ticket by ID
    const fetchTicket = async (ticketId, employeeId) => {
        if (!ticketId) {
            ticket.value = null;
            loading.value = false;
            return;
        }
        
        // Clear current ticket and set loading state
        clearTicket();
        
        try {
            // Use the endpoint from API_ENDPOINTS
            const url = API_ENDPOINTS.fetchTicketById
                .replace('{ticketId}', ticketId)
                .replace('{employeeId}', employeeId || 0);
            
            // Make the API call
            const response = await axiosClient.get(url);
            
            // The API returns a single ticket object directly
            if (response.data) {
                ticket.value = response.data;
            } else {
                error.value = 'Ticket not found';
            }
        } catch (err) {
            console.error('Error fetching ticket:', err);
            error.value = err.message || 'Failed to fetch ticket';
        } finally {
            // Set loading state to false when done
            loading.value = false;
        }
    };

    // Function to publish a post in the ticket
    const publishPost = async (ticket, postIndex) => {
        console.log('publishPost', ticket, postIndex);
        try {
            // Set publishing state to true
            publishingStates.value[`${ticket.id}-${postIndex}`] = true;
            // Also set the main loading state to true
            loading.value = true;

            // Send email (using the email sender)
            const { sendEmail } = useEmailSender();
            await sendEmail(
                ticket.employee.email,
                //'hussainbtk@gmail.com',
                `Complaint #${ticket.id} ${ticket.posts[postIndex].type}`,
                ticket
            );

            // Prepare data for the API
            const publishData = {
                ticket_id: ticket.id,
                post_index: postIndex,
                mailer_status: true,
                status: ticket.posts[postIndex].status,
                timestamp: new Date().toISOString()
            };

            // Call the API endpoint to publish the ticket
            await axiosClient.put(API_ENDPOINTS.publishTicket, publishData);

            // Fetch fresh ticket data to get the updated state
            await fetchTicket(ticket.id, ticket.employee?.id);

        } catch (error) {
            console.error('Error publishing post:', error);
            throw error;
        } finally {
            publishingStates.value[`${ticket.id}-${postIndex}`] = false;
            // Note: We don't set loading.value = false here because fetchTicket will do that
        }
    };

    // Function to delete a post
    const deletePost = async (ticketId, postIndex) => {
        try {
            publishingStates.value[`${ticketId}-${postIndex}`] = true;
            // Also set the main loading state to true
            loading.value = true;
            
            // Call API to delete the post
            await axiosClient.delete(API_ENDPOINTS.deletePost, {
                data: {
                    ticket_id: ticketId,
                    post_index: postIndex,
                    mailer_status: false
                }
            });
            
            // Refresh the ticket data
            await fetchTicket(ticketId, ticket.value?.employee?.id);
            
        } catch (error) {
            console.error('Error deleting post:', error);
            throw error;
        } finally {
            publishingStates.value[`${ticketId}-${postIndex}`] = false;
            // Note: We don't set loading.value = false here because fetchTicket will do that
        }
    };

    // Function to delete a ticket
    const deleteTicket = async (ticketId, employeeId, complaintDate) => {
        try {
            loading.value = true;
            await axiosClient.delete(API_ENDPOINTS.deleteTicket, {
                data: { ticket_id: ticketId, employee_id: employeeId, complaint_date: complaintDate }
            });
            ticket.value = null;
        } catch (error) {
            console.error('Error deleting ticket:', error);
            throw error;
        } finally {
            loading.value = false;
        }
    };

    return {
        ticket,
        loading,
        error,
        publishingStates,
        clearTicket,
        fetchTicket,
        publishPost,
        deletePost,
        deleteTicket
    };
} 